import type { SxProps, Theme } from '@mui/material/styles';
import { Color } from 'hephaestus/core';
import { useTheme } from 'hephaestus/web';

export enum SideDrawerSize {
  Default = 'default',
  Sm = 'sm',
  Md = 'md',
  Lg = 'lg',
}

/** Fixed drawer widths per breakpoint (`xs` is always full width). */
export const SIDE_DRAWER_WIDTH_BY_SIZE: Record<
  SideDrawerSize,
  { xs: string; sm: number; md: number; lg: number }
> = {
  [SideDrawerSize.Default]: { xs: '100%', sm: 480, md: 480, lg: 480 },
  [SideDrawerSize.Sm]: { xs: '100%', sm: 360, md: 400, lg: 400 },
  [SideDrawerSize.Md]: { xs: '100%', sm: 480, md: 560, lg: 600 },
  [SideDrawerSize.Lg]: { xs: '100%', sm: 480, md: 640, lg: 720 },
};

export type SideDrawerTokens = {
  sideDrawer: {
    drawerRootSx: SxProps<Theme>;
    headerSx: SxProps<Theme>;
    bodySx: SxProps<Theme>;
    footerSx: SxProps<Theme>;
  };
};

/**
 * Layout and surface tokens for {@link SideDrawer}. Keeps default styling out of the component
 * so consumers still override via the `sx` prop on `Drawer`.
 */
export const useSideDrawerTokens = (
  size: SideDrawerSize = SideDrawerSize.Default,
): SideDrawerTokens => {
  const theme = useTheme();

  return {
    sideDrawer: {
      drawerRootSx: {
        '& .MuiDrawer-paper': {
          display: 'flex',
          flexDirection: 'column',
          width: SIDE_DRAWER_WIDTH_BY_SIZE[size],
          maxWidth: '100%',
          height: '100%',
          bgcolor: theme.color.background,
          borderLeft: `1px solid ${theme.color.outlineVariant}`,
          boxShadow: (theme: Theme) =>
            `-8px 0 24px ${Color(theme.palette.common.black)
              .alpha(theme.palette.mode === 'dark' ? 0.35 : 0.12)
              .string()}`,
        },
      },
      headerSx: {
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        px: 2,
        py: 1.5,
        borderBottom: `1px solid ${theme.color.outlineVariant}`,
      },
      bodySx: {
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        px: 2,
        py: 2,
      },
      footerSx: {
        flexShrink: 0,
        borderTop: `1px solid ${theme.color.outlineVariant}`,
        p: 1.5,
      },
    },
  };
};
